import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { TagTemplateElement } from 'src/app/_model/tag-template-element';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { TagStatesDaoService } from '../../dao/tag-states-dao.service';
import { TagStatesService } from '../tag-states.service';
import { APILevel } from 'src/app/_services/sync/api-level';

@Injectable()
export class TagStatesLogicService implements TagStatesService {
  tagTemplatesWithStateForPhotodocForServiceJob$ = new BehaviorSubject<
    TagTemplateElement[]
  >([]);
  tagStatesForPhotodocForServiceJobs$ = new BehaviorSubject<
    TagTemplateElement[]
  >([]);

  constructor(
    private logger: LoggingService,
    private authenticationService: AuthenticationService,
    private servicejobsService: ServicejobsService,
    private remoteActionService: RemoteActionService,
    private tagStatesDaoService: TagStatesDaoService,
  ) {}

  public async presync(): Promise<void> {}
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.loadRemoteTagStates(apiLevel, syncKey);
  }
  public async postsync(): Promise<void> {}

  private async loadRemoteTagStates(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    this.logger.log(this.constructor.name, '- Load new tag states from IMS');

    try {
      const tagStates = await this.loadRemoteTagStatesBatch(syncKey);
      this.logger.log(
        this.constructor.name,
        `Finished loading all tag states (${tagStates.length} Total)`,
      );
      this.persistLocalTagStates(tagStates);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading tag states: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteTagStatesBatch(syncKey: string): Promise<any[]> {
    const sjobList = this.servicejobsService.servicejobs$
      .getValue()
      .map((sjob) => {
        return sjob.getImsId();
      })
      .join(',');

    const data = await this.remoteActionService.sendRemoteAction(
      new RemoteAction({
        endpoint: 'synctagstates',
        cachable: false,
        fifo: false,
        authenticationRequired: true,
        data: {
          sjoblist: sjobList,
          synckey: syncKey
        },
      }),
    );

    this.logger.log(
      this.constructor.name,
      'Downloaded Batch tag states: ',
      data.length,
      'Total',
    );

    return data;
  }

  private async persistLocalTagStates(tagStates: any[]): Promise<void> {
    await this.tagStatesDaoService.persistLocalTagStates(tagStates);
    this.tagStatesForPhotodocForServiceJobs$.next(tagStates);
  }

  async incrementTagStateCounter(
    tagHierarchy: TagTemplateElement[],
    amount: number,
    notPossible: boolean,
    ownerid: number,
    ownerref: string,
  ): Promise<void> {
    // update tag state counter immediately so the user has the updated counter

    const states = await this.tagStatesDaoService.loadLocalTagStates();
    tagHierarchy.forEach((tag) => {
      const state = states.find(
        (state) => state.tagtemplateelementid === tag.getImsId(),
      );
      if (state) {
        state.counter = state.counter + amount;
      } else {
        states.push({
          tagtemplateelementid: tag.getImsId(),
          label: '',
          counter: amount,
          notpossible: notPossible,
          ownerid: ownerid,
          ownerref: ownerref,
        });
      }
    });

    this.persistLocalTagStates(states);
  }

  allTagsSet(): boolean {
    // check if all tags are set
    // tag is considered to be valid:
    // - if not required
    // - if required and set
    // - if required and notpossible flag is set

    const tagsWithState =
      this.tagTemplatesWithStateForPhotodocForServiceJob$.getValue();
    for (const tag of tagsWithState) {
      // if state is not set there are no entries in taglookup table
      if (tag.required && tag[TagStatesService.TAG_STATE] == null) {
        return false;
      }
    }
    return true;
  }
}
